/**
 * @Author: wuyang
 * @Date: 2024/1/9
 * @Description: ""
 */
import {
	BaseAbstractClass,
	IBaseAbstractClassProps
} from '@/helper/base/BaseAbstractClass'
import { atomWithStorage } from 'jotai/utils'
import { getUserInfo } from '@/api/common'
import { atom } from 'jotai'
import { IBusinessInfo, IUserInfo } from '@/types/user'
import { IBaseEventMitt } from '@/helper/base/BaseAtomMethodClass'
import { ISocketMessagePayStatus } from '@/types/socket'
import { getBisInfo, userLogout } from '@/api/login'

interface IUserInfoServiceMittProps extends IBaseEventMitt {
	onLogin: IUserInfo
	onLogout: IUserInfo
	onPayStatus: ISocketMessagePayStatus
}

interface IUserInfoServiceProps
	extends IBaseAbstractClassProps<IUserInfoServiceMittProps> { }

export class UserInfoService extends BaseAbstractClass<
	IUserInfoServiceProps,
	IUserInfoServiceMittProps
> {
	// 登录信息 需要持久化
	token = this.genAtomMethod(
		atomWithStorage('token', '', undefined, {
			getOnInit: true
		})
	)

	// 是否已经登录
	isLogin = this.genAtomMethod(
		atomWithStorage('isLogin', false, undefined, {
			getOnInit: true
		})
	)

	// openId
	openId = this.genAtomMethod(
		atomWithStorage('openId', '', undefined, {
			getOnInit: true
		})
	)

	// 未关注公众号登录时跳过扫码
	skipScan = this.genAtomMethod(
		atomWithStorage('skipScan', false, undefined, {
			getOnInit: true
		})
	)

	// 企业信息
	businessInfo = this.genAtomMethod(atom<IBusinessInfo | null>(null))

	userInfo = this.genAtomMethod(
		atom<IUserInfo | null>({
			avatar: '',
			nickName: '',
			phone: '',
			userId: '',
			residueScore: 0,
			packageName: '',
			password: '',
			passwordUpdateTime: '',
			passwordRemindDays: 0,
			isPassword: false,
			userPackageAndBagVOs: [],
			packageId: '',
			isDefaultPackage: 1,
			wxOpenId: '',
			residualIntegral: 0,
			expirationTime: '',
			tariffEndTime: '',
			residueScoreValue: 0,
			equityDescriptionList: [],
			user: {},
			team: null,
			teamId: 0,
			currentMode: 1,
			profession: '',
			industry: ''

		})
	)

	// 是否为 vip 用户
	isVip = this.selectAtom(
		this.userInfo,
		(userInfo) => userInfo?.isDefaultPackage !== 1
	)

	// 是否可以下载4K图片
	canDownload4K = this.selectAtom(
		this.userInfo,
		(userInfo) => userInfo?.fourKDown === 1
	)

	constructor(props: IUserInfoServiceProps) {
		super(props)
		this.init(props)
	}

	async asyncInit(props: IUserInfoServiceProps): Promise<void> {
		super.asyncInit(props)
		this.refreshBusinessInfo()
		if (this.isLogin.get()) {
			this.refresh()
				.then((res) => {
					const userInfo = this.userInfo.get()
					if (userInfo) {
						this.mitt.emit('onLogin', userInfo)
					}
				})
				.catch((err) => { })
		}
	}

	// 刷新我的用户信息
	async refresh() {
		if (this.token.get()) {
			return getUserInfo()
				.then((res) => {
					this.userInfo.set(res)
					this.openId.set(res?.wxOpenId ?? '')
				})
				.catch((err) => {
					return err
				})
		} else {
			this.userInfo.set(null)
			this.isLogin.set(false)
			this.openId.set('')
		}
	}

	// 获取企业信息接口
	refreshBusinessInfo() {
		getBisInfo()
			.then((res) => {
				this.businessInfo.set({
					recordNumber: res.recordNumber,
					uploadGuide: res.tBaseinfoContactusList,
					usedGuide: res.tBaseinfoUseguideList,
					name: res.companyName,
					customVipTitle: res.customizedBusinessName,
					customVipDesc: res.customizedBusinessContent
				})
			})
			.catch((err) => {
				console.error('getBisInfo err', err)
			})
	}

	// 登录接口
	login() {
		// 如果之前没有登录过，则调用登录事件
		if (!this.isLogin.get()) {
			this.isLogin.set(true)
			const userInfo = this.userInfo.get()
			if (userInfo) {
				this.mitt.emit('onLogin', userInfo)
			}
		}
	}

	logoutByOverTime() {
		const userInfo = this.userInfo.get()
		if (userInfo) {
			this.mitt.emit('onLogout', userInfo)
		}
		this.token.set('')
		this.isLogin.set(false)
		this.skipScan.set(false)
		this.openId.set('')
		this.userInfo.set(null)
	}

	// 退出登录
	logout() {
		userLogout().catch((err) => { })
		this.logoutByOverTime()
	}

	// 重连socket
	async reconnectSocket() {
		await this.refresh()
		// 重新登录
		const userInfo = this.userInfo.get()
		if (userInfo) {
			this.mitt.emit('onLogin', userInfo)
		}
	}
}
